import { useState } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { Card } from "../client/components/ui/card";
import { Badge } from "../client/components/ui/badge";
import { Button } from "../client/components/ui/button";
import { Checkbox } from "../client/components/ui/checkbox";
import type { OfferVM, FirmVM } from "./lib/types";
import { isPromoActive, effectivePrice, totalCost } from "./lib/derive";
import { buildAffiliateUrl } from "./lib/affiliate";
import { formatUsd } from "./lib/format";
import { drawdownTypeLabel, ratingClass } from "./lib/labels";

interface OfferCardProps {
  offer: OfferVM;
  firm: FirmVM;
  now: Date;
  isSelected: boolean;
  onToggleCompare: (offerId: string) => void;
}

function KeyValue({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-sm text-foreground">{children}</div>
    </div>
  );
}

export default function ComparatorOfferCard({
  offer,
  firm,
  now,
  isSelected,
  onToggleCompare,
}: OfferCardProps) {
  const t = useTranslation();
  const [copied, setCopied] = useState(false);

  const promo = isPromoActive(offer, now);
  const eff = effectivePrice(offer, now);
  const url = buildAffiliateUrl(offer, firm);

  const copyPromoCode = async () => {
    if (!firm.promoCode) return;
    try {
      await navigator.clipboard.writeText(firm.promoCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — silently ignore */
    }
  };

  return (
    <Card className="p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Checkbox
          className="mt-1"
          checked={isSelected}
          onCheckedChange={() => onToggleCompare(offer.id)}
        />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">{firm.name}</div>
          <div className="text-sm text-muted-foreground">{offer.planName}</div>
        </div>
        <Badge variant="outline" className="font-mono shrink-0">
          {offer.accountSizeK}k
        </Badge>
      </div>

      {/* Price */}
      <div className="space-y-1">
        {promo ? (
          <>
            <div className="flex items-center gap-2">
              <span className="font-mono text-lg font-semibold text-foreground">
                {formatUsd(eff)}
              </span>
              <Badge className="bg-emerald-500/15 text-emerald-500">
                -{offer.discountPercent}%
              </Badge>
              <span className="line-through text-muted-foreground font-mono text-xs">
                {formatUsd(offer.priceUsd)}
              </span>
            </div>
            {firm.promoCode && (
              <button
                type="button"
                onClick={copyPromoCode}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                {copied ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
                {copied ? t.comparator.codeCopied : t.comparator.copyCode}
              </button>
            )}
            {offer.discountExpiresAt && (
              <div className="text-xs text-muted-foreground">
                {t.comparator.promoExpires.replace(
                  "{date}",
                  new Date(offer.discountExpiresAt).toLocaleDateString(),
                )}
              </div>
            )}
          </>
        ) : (
          <span className="font-mono text-lg font-semibold text-foreground">
            {formatUsd(offer.priceUsd)}
          </span>
        )}
      </div>

      {/* Key/value grid */}
      <div className="grid grid-cols-2 gap-3">
        <KeyValue label={t.comparator.colBilling}>
          {offer.billingType === "one_time"
            ? t.comparator.billingOneTime
            : t.comparator.billingMonthly}
        </KeyValue>

        <KeyValue label={t.comparator.colActivation}>
          {offer.activationFeeUsd === 0 ? (
            <span className="font-mono text-emerald-500">{t.comparator.free}</span>
          ) : (
            <div>
              <span className="font-mono">
                {formatUsd(offer.activationFeeUsd)}
              </span>
              <div className="text-xs text-muted-foreground font-mono">
                {t.comparator.total}: {formatUsd(totalCost(offer, now))}
              </div>
            </div>
          )}
        </KeyValue>

        <KeyValue label={t.comparator.colPlatforms}>
          <div className="flex flex-wrap gap-1">
            {offer.platforms.map((p) => (
              <Badge key={p} variant="outline" className="text-[10px]">
                {p}
              </Badge>
            ))}
          </div>
        </KeyValue>

        <KeyValue label={t.comparator.colDrawdown}>
          {offer.drawdownUsd != null ? (
            <div className="flex flex-col gap-1">
              <span className="font-mono">
                {formatUsd(offer.drawdownUsd)}
              </span>
              {offer.drawdownType && (
                <Badge variant="outline" className="w-fit text-[10px]">
                  {drawdownTypeLabel(t, offer.drawdownType)}
                </Badge>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </KeyValue>

        <KeyValue label={t.comparator.colTarget}>
          {offer.profitTargetUsd != null ? (
            <span className="font-mono">
              {formatUsd(offer.profitTargetUsd)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </KeyValue>

        <KeyValue label={t.comparator.colRoundTrip}>
          <span className="font-mono">
            $
            {offer.roundTripMicroUsd != null
              ? offer.roundTripMicroUsd.toLocaleString("en-US")
              : "—"}{" "}
            / $
            {offer.roundTripMiniUsd != null
              ? offer.roundTripMiniUsd.toLocaleString("en-US")
              : "—"}
          </span>
        </KeyValue>

        <KeyValue label={t.comparator.colRating}>
          <span className={`font-mono ${ratingClass(firm.rating)}`}>
            {firm.rating ?? "—"}
          </span>
        </KeyValue>
      </div>

      {/* Action */}
      {url ? (
        <a href={url} target="_blank" rel="sponsored nofollow noopener noreferrer" className="block">
          <Button size="sm" className="w-full">
            {t.comparator.startChallenge}
            <ExternalLink className="ml-1 h-3 w-3" />
          </Button>
        </a>
      ) : (
        <Button size="sm" className="w-full" disabled>
          {t.comparator.startChallenge}
        </Button>
      )}
    </Card>
  );
}
